import { CircularProgress, FormControl } from '@mui/material';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import React from 'react';
import { UseControllerProps, useController } from 'react-hook-form';

export interface IDropDownOptionsWithDescription {
    id: string | number;
    value: string;
    description?: string;
}

interface IProps extends UseControllerProps {
    label: string;
    items: IDropDownOptionsWithDescription[];
    size?: 'small' | 'medium';
    disabled?: boolean;
    loading?: boolean;
    disabledItems?: (string | number)[];
    onChange?: (value: any) => void;
    onInputFieldChange?: (value: string) => void;
    style?: React.CSSProperties;
    useManualFiltering?: boolean;
}

const AutocompleteWithInfo = (props: IProps) => {
    const filterOptions = (options: IDropDownOptionsWithDescription[], { inputValue }: { inputValue: string }) => {
        return options.filter((option) => {
            const mainTextMatch = option.value.toLowerCase().includes(inputValue.toLowerCase());
            const descriptionMatch = option.description?.toLowerCase().includes(inputValue.toLowerCase()) ?? false;
            return mainTextMatch || descriptionMatch;
        });
    };
    const { fieldState, field } = useController({ ...props, defaultValue: null });

    const handleInputChange = (event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => {
        if (reason === 'input' || reason === 'clear') {
            if (props.onInputFieldChange) {
                props.onInputFieldChange(value);
            }
        } else {
            if (props.onInputFieldChange) {
                props.onInputFieldChange('');
            }
        }
    };

    return (
        <FormControl fullWidth error={!!fieldState.error}>
            <Autocomplete
                {...props}
                {...field}
                onChange={(event, value) => {
                    field.onChange(value);
                    if (props.onChange) {
                        props.onChange(value);
                    }
                }}
                onInputChange={handleInputChange}
                isOptionEqualToValue={(option, value: any) => option.id === value.id}
                options={props.items}
                filterOptions={(options, { inputValue }) => {
                    if (props.useManualFiltering === true) {
                        return options;
                    } else {
                        return filterOptions(options, { inputValue }); 
                    }
                }}
                getOptionLabel={(option) => option.value}
                loading={props.loading}
                renderOption={(props, option) => (
                    <li {...props} key={`${option.id}-${option.description}`}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div
                                    style={{
                                        fontSize: option.description ? undefined : 'inherit',
                                        fontWeight: option.description ? 'bold' : 'normal',
                                    }}
                                >
                                    {option.value}
                                </div>
                                {option.description && <div>{option.description}</div>}
                            </div>
                        </div>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        helperText={fieldState.error?.message}
                        label={props.label}
                        variant="outlined"
                        error={!!fieldState.error}
                        size={props.size || 'medium'}
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            style: {
                                paddingTop: '10px',
                                paddingBottom: '10px',
                            },
                            endAdornment: (
                                <React.Fragment>
                                    {props.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                    />
                )}

            />
        </FormControl>
    );
};

export default AutocompleteWithInfo;
